import API from '../api/api';
import GlobalContext from './GlobalContext';
import { useLocation, useHistory } from 'react-router';
import { TRegister3rdPartyPost } from '../api/types';
import { ELinks, EFamilyAccountType, ERoutes, noNameAvailable, maxNameLength, EMixpanelEvent } from '../util/constants';
import { TCreateFamilyAccountRouterState } from './CreateFamilyAccountContext';
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react';
import mixpanel from 'mixpanel-browser';

export type TRegister3rdPartyRouterState = {
    prePopulateForm?: {
        canShowUsername: boolean;
        firstName: string;
        lastName: string;
        country: string;
    };
};

type TRegister3rdPartyContext = {
    register3rdPartyUiState: ERegister3rdPartyUiState;
    setRegister3rdPartyUiState: (state: ERegister3rdPartyUiState) => void;
    canShowUsername: boolean;
    firstName: string;
    setFirstName: (firstName: string) => void;
    firstNameIsValid: boolean;
    firstNameValidationMessage: string;
    lastName: string;
    setLastName: (lastName: string) => void;
    lastNameIsValid: boolean;
    lastNameValidationMessage: string;
    selectedCountryCode: string;
    setSelectedCountryCode: (country: string) => void;
    countryIsValid: boolean;
    submitRegisterForm: () => void;
    addFamily: () => void;
    skipAddFamily: () => void;
    redirectUrl: string;
};

export enum ERegister3rdPartyUiState {
    default,
    initialized,
    ready,
    loading,
    confirm,
    error,
    success,
}

const Register3rdPartyContext = createContext<TRegister3rdPartyContext | undefined>(undefined);

export const Register3rdPartyProvider: FC = ({ children }) => {
    const {
        csrfToken,
        getCsrfToken,
        getCoBranding,
        countriesList,
        getCountriesList,
        language,
        subscriberId,
        setSubscriberId,
    } = useContext(GlobalContext);
    const [register3rdPartyUiState, setRegister3rdPartyUiState] = useState(ERegister3rdPartyUiState.default);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [canShowUsername, setCanShowUsername] = useState(true);
    const [firstName, setFirstName] = useState('');
    const [firstNameIsValid, setFirstNameIsValid] = useState(true);
    const [firstNameValidationMessage, setFirstNameValidationMessage] = useState('');
    const [lastName, setLastName] = useState('');
    const [lastNameIsValid, setLastNameIsValid] = useState(true);
    const [lastNameValidationMessage, setLastNameValidationMessage] = useState('');
    const [countryIsValid, setCountryIsValid] = useState(true);
    const [selectedCountryCode, setSelectedCountryCode] = useState('');
    const history = useHistory();
    const { search: queryParams } = useLocation();
    const { state: routerState = {} }: { state: TRegister3rdPartyRouterState } = useLocation();
    const isPrePopulated = useMemo(() => routerState.hasOwnProperty('prePopulateForm'), []);

    const getInitialData = async () => {
        await getCsrfToken();
        !countriesList.length && (await getCountriesList());
        try {
            const response = await API.get.setupRegister3rdParty();
            if (!isPrePopulated) {
                const { givenName, surname, subscriberId } = response.data;
                givenName && surname && setCanShowUsername(false);
                givenName && setFirstName(givenName);
                surname && setLastName(surname);
                subscriberId && setSubscriberId(subscriberId.toString());
            } else {
                const { canShowUsername, firstName, lastName, country } = routerState.prePopulateForm;
                const { subscriberId } = response.data;
                setCanShowUsername(canShowUsername);
                setFirstName(firstName);
                setLastName(lastName);
                setSelectedCountryCode(country);
                subscriberId && setSubscriberId(subscriberId.toString());
            }
            setRegister3rdPartyUiState(ERegister3rdPartyUiState.initialized);
        } catch (error) {
            setRegister3rdPartyUiState(ERegister3rdPartyUiState.error);
            console.error('Failed to get 3rd party data');
        }
    };

    const validateRegisterForm = () => {
        let formIsValid = true;
        let firstNameIsValid = true;
        let firstNameValidationMessage = '';
        let lastNameIsValid = true;
        let lastNameValidationMessage = '';
        let countryIsValid = true;
        if (firstName.toUpperCase() === noNameAvailable) {
            formIsValid = false;
            firstNameIsValid = false;
            firstNameValidationMessage = language.validation.firstNameInvalid;
        }
        if (lastName.toUpperCase() === noNameAvailable) {
            formIsValid = false;
            lastNameIsValid = false;
            lastNameValidationMessage = language.validation.lastNameInvalid;
        }
        if (firstName.length > maxNameLength) {
            formIsValid = false;
            firstNameIsValid = false;
            firstNameValidationMessage = language.validation.firstNameMaxLength;
        }
        if (lastName.length > maxNameLength) {
            formIsValid = false;
            lastNameIsValid = false;
            lastNameValidationMessage = language.validation.lastNameMaxLength;
        }
        if (!selectedCountryCode) {
            formIsValid = false;
            countryIsValid = false;
        }
        setFirstNameIsValid(firstNameIsValid);
        setFirstNameValidationMessage(firstNameValidationMessage);
        setLastNameIsValid(lastNameIsValid);
        setLastNameValidationMessage(lastNameValidationMessage);
        setCountryIsValid(countryIsValid);
        return formIsValid;
    };

    const addFamily = () => {
        const formIsValid = validateRegisterForm();
        if (formIsValid) {
            const userData = { canShowUsername, firstName, lastName, country: selectedCountryCode };
            const accessToken = queryParams;
            const register3rdPartyRouterState: TRegister3rdPartyRouterState = {
                prePopulateForm: userData,
            };
            const createFamilyAccountRouterState: TCreateFamilyAccountRouterState = {
                accessToken,
                familyAccountType: EFamilyAccountType.thirdParty,
                primaryUser: userData,
            };
            // replace history state to preserve form values when navigating via back button
            history.replace({
                state: register3rdPartyRouterState,
            });
            // then go to crete family account screen
            history.push({
                pathname: ERoutes.createFamilyAccount,
                state: createFamilyAccountRouterState,
            });
        }
    };

    const skipAddFamily = () => {
        const formIsValid = validateRegisterForm();
        if (formIsValid) {
            setRegister3rdPartyUiState(ERegister3rdPartyUiState.confirm);
        }
    };

    const submitRegisterForm = async () => {
        const formIsValid = validateRegisterForm();
        if (formIsValid) {
            setRegister3rdPartyUiState(ERegister3rdPartyUiState.loading);
            const postObject: TRegister3rdPartyPost = {
                givenName: firstName ? firstName : noNameAvailable,
                surname: lastName ? lastName : noNameAvailable,
                countryCode: selectedCountryCode,
            };
            try {
                const response = await API.post.register3rdParty(postObject, queryParams, csrfToken);
                const redirectUrl = response.data.redirectUrl ? response.data.redirectUrl : ELinks.userPortal;
                mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'consumer_create_account' });
                setRedirectUrl(redirectUrl);
                setRegister3rdPartyUiState(ERegister3rdPartyUiState.success);
            } catch {
                mixpanel.track(EMixpanelEvent.actionFailed, { key: 'consumer_create_account' });
                setRegister3rdPartyUiState(ERegister3rdPartyUiState.error);
            }
        }
    };

    useEffect(() => {
        if (countriesList.length && !selectedCountryCode) {
            setSelectedCountryCode(countriesList[0].code);
        }
    }, [countriesList]);

    useEffect(() => {
        if (register3rdPartyUiState === ERegister3rdPartyUiState.default) {
            getInitialData();
        } else if (register3rdPartyUiState === ERegister3rdPartyUiState.initialized) {
            if (subscriberId) {
                getCoBranding().then(() => {
                    setRegister3rdPartyUiState(ERegister3rdPartyUiState.ready);
                });
            } else {
                setRegister3rdPartyUiState(ERegister3rdPartyUiState.ready);
            }
        }
    }, [register3rdPartyUiState]);

    return (
        <Register3rdPartyContext.Provider
            value={{
                register3rdPartyUiState,
                setRegister3rdPartyUiState,
                canShowUsername,
                firstName,
                setFirstName,
                firstNameIsValid,
                firstNameValidationMessage,
                lastName,
                setLastName,
                lastNameIsValid,
                lastNameValidationMessage,
                selectedCountryCode,
                setSelectedCountryCode,
                countryIsValid,
                submitRegisterForm,
                addFamily,
                skipAddFamily,
                redirectUrl,
            }}
        >
            {children}
        </Register3rdPartyContext.Provider>
    );
};

export default Register3rdPartyContext;
